import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import deepEqual from 'react-fast-compare'
import { Alert, Flex, Input } from 'antd'
import { Well } from '@pushly/aqe/lib/components'
import { DeliveryChannelSelector } from '@pushly/aqe/lib/components/delivery-channel-selector/delivery-channel-selector'
import { PQueryBuilderProvider, VERTICAL_WELL_GAP } from '@pushly/aqe'
import { useService } from '@pushly/aqe/lib/hooks'
import type { DeliveryChannel } from '@pushly/models/lib/enums/delivery-channels.enum'
import { SegmentSource } from '@pushly/models/lib/enums/segment-source'
import type { PQueryBuilderField } from '@pushly/models/lib/interfaces/pquery-builder-field'
import type { SegmentQueryField } from '@pushly/models/lib/interfaces/segment-query-field'
import { SegmentQueryFieldsService } from '../../services/segment-query-fields.service'
import { SegmentService } from '../../services/segment'
import { OrgSegmentService } from '../../services/org-segment'
import { BatchService } from '../../services/batch'
import { AccountService } from '../../services/account'
import { SegmentModel } from '../../models/segments/segment.model'
import { OrgSegmentModel } from '../../models/segments/org-segment.model'
import { AppState } from '../../stores/app'
import { getEnabledDeliveryChannels } from '../../_utils/domain'
import { getAccountEnabledDeliveryChannels } from '../../_utils/account'
import { onResponseError403 } from '../../_utils/on-response-error-403'
import { useSegmentQueryFields } from './use-segment-query-fields'
import { canSubmitSegment, useUnsavedChangesGuard } from './use-unsaved-changes-guard'
import {
    buildSegmentSaveDto,
    didSaveSucceed,
    getResponseRecord,
    getResponseRecordId,
    hasSaveChanges,
    toSegmentId,
} from './save'
import type { AnySegment } from './save'
import { SegmentBuilderFooter } from './segment-builder-footer'
import { SegmentIconField } from './segment-icon-field'
import type { BuilderSubmission, SegmentBuilderFooterHandle } from './segment-builder-footer'
import { ReadOnlyNotice } from './read-only-notice'
import {
    describeMissingExpressions,
    describeMissingWindows,
    findConditionsMissingExpression,
    findConditionsMissingWindow,
} from './required-expression'
import { SEGMENT_VALUE_INPUTS, SEGMENT_VALUE_VALIDATORS, useSegmentValueSummaries } from './value-inputs'
import { SegmentValueInputProvider } from './value-inputs/segment-value-input-provider'
import { useSegmentValueInputContext } from './value-inputs/segment-value-input-context'
import { collapseAppVersionChannels, expandAppVersionChannels } from './app-version'
import { SegmentReachEstimate } from './reach/segment-reach-estimate'
import { SegmentOrgReachEstimate } from './reach/segment-org-reach-estimate'
import type { EstimateOrgReach } from './reach/segment-org-reach-estimate'
import { estimateOrgReach } from './reach/estimate-org-reach'
import type { OrgReachBatchService } from './reach/estimate-org-reach'
import { reduceOrgCalculatedFields } from './reach/org-calculated-fields'
import type { OrgCalculatedField } from './reach/org-calculated-fields'
import { isUnsafeExecution } from './reach/unsafe-execution'
import type { ReachCalculatedField } from './reach/unsafe-execution'
import { getUnsafeExecutionText } from './reach/unsafe-execution'
import { domainOptionsSort } from '../../_utils/domain'
import { collectUnsupportedProperties, pruneUnsupportedConditions } from './unsupported-description'
import { filterSegmentQueryFields } from './use-segment-query-fields'
import DomainAudienceSelect from '../domain-audience-select/domain-audience-select'
import type { DropdownViewSelectOption } from '../dropdown-view-with-select-all/dropdown-view-with-select-all'
import { AbilityAction } from '../../enums/ability-action.enum'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { asCaslSubject } from '../../stores/app-ability'
import { antdStaticApi } from '../../providers/antd-app-provider/antd-static-api'

interface OrgProps {
    level: 'org'
    orgId: number
}

interface DomainProps {
    level: 'domain'
    domainId: number
}

/**
 * The parts of each service this component reads and writes through.
 *
 * Narrow on purpose: they are the seam a caller substitutes at, and naming only
 * what is used keeps a stand-in from having to imitate a whole service.
 */
export type SegmentQueryFieldsSource = Pick<
    SegmentQueryFieldsService,
    'getDomainFieldsCached' | 'getAccountFieldsCached'
>
export type DomainSegmentStore = Pick<
    SegmentService,
    | 'fetchSegmentById'
    | 'createSegmentForDomainId'
    | 'updateSegmentById'
    | 'estimateReachByQuery'
    | 'fetchCalculatedFieldsByDomainId'
>
export type OrgSegmentStore = Pick<
    OrgSegmentService,
    'fetch' | 'create' | 'update' | 'fetchCalculatedFields'
>
/**
 * Where the per-level facts about the current user's domains are read from.
 *
 * The loaded domain records already carry each domain's own channel flags and its
 * parent account's, so the channels a segment may target are known from what the
 * app has in memory — no per-segment fetch. The same records, paired with the
 * ability store, are what the org domain options are drawn from. Narrowed to the
 * two fields that hold this so a stand-in supplies them rather than a whole store.
 */
export type SegmentChannelSource = Pick<AppState, 'currentUserDomains' | 'abilityStore'>

export type SegmentQueryBuilderProps = (OrgProps | DomainProps) & {
    readonly?: boolean
    /**
     * Substitutes for the services this component would otherwise resolve from
     * the container. Whatever is passed has to stay the same object for as long
     * as the component is mounted: the fetches are keyed on service identity, so
     * a fresh object per render would re-run them.
     */
    fieldsService?: SegmentQueryFieldsSource
    segmentService?: DomainSegmentStore
    orgSegmentService?: OrgSegmentStore
    /**
     * Substitutes for the batch service the org reach estimate fans its per-domain
     * requests through. Same identity contract as the services above.
     */
    batchService?: OrgReachBatchService
    /**
     * Substitutes for the account service the org level reads its media quality from, which sets how
     * the icon uploader compresses. Same identity contract as the services above.
     */
    accountService?: Pick<AccountService, 'fetchById'>
    /**
     * Substitutes for the loaded-domains store the enabled channels are derived
     * from. Same identity contract as the services above.
     */
    channelSource?: SegmentChannelSource
    segmentId?: number
    templateId?: number
    /**
     * Rendered between the details and the conditions, as a sibling of the wells rather than inside
     * one.
     *
     * Supplied by the consumer rather than placed here, because which surfaces make an announcement is
     * a product decision and this component is rendered on several — the segment screen, a notification
     * drawer, a journey audience, an app message. A notice this component chose for itself would appear
     * on all of them.
     */
    notice?: React.ReactNode
    mode?: 'standard' | 'drawer'
    name?: string
    source?: SegmentSource
    channels?: DeliveryChannel[]
    hideChannelsSelector?: boolean
    onSaved?: (segment: SegmentModel | OrgSegmentModel) => void
    onCancel?: () => void
}

/**
 * Imperative surface for consumers that render the builder without a footer —
 * a drawer supplies its own buttons and drives the component through this.
 */
export interface SegmentQueryBuilderHandle {
    /**
     * Runs the same validation gate as the footer submit, saves the segment when
     * it passes, and resolves whether it was saved. Resolving `false` means
     * nothing was persisted — either the component is now showing why it refused,
     * or the request failed and the service has already notified about it.
     */
    submit: () => Promise<boolean>
    /**
     * True when a save from the current state would send something the stored
     * record does not already hold — so, whether leaving now would lose work.
     *
     * Every field a save can carry counts, the name as much as the conditions. A
     * consumer uses this to decide whether an exit needs confirming, so anything it
     * leaves out is something that exit discards without asking.
     */
    isDirty: () => boolean
}

const NO_CATALOG: SegmentQueryField[] = []
const NO_CHANNELS: DeliveryChannel[] = []
const NO_DOMAIN_OPTIONS: DropdownViewSelectOption<number>[] = []
const NO_DOMAIN_IDS: number[] = []

/**
 * A query with no criteria, as a new object each time.
 *
 * Every result is handed to something that keeps it — the builder holds the
 * document it is opened with, and a segment model holds the one it is built with
 * — so a single shared instance would put two owners on one object.
 *
 * One empty group sits on the canvas rather than leaving the root bare. A segment
 * reads as groups combined with AND, each group holding alternatives combined with
 * OR, and nearly four in ten stored segments use a group of real alternatives — so
 * the container is where the work starts, and making the reader create it first
 * reads as configuration rather than as building the query.
 *
 * The group is an `or`, which is what makes it useful: rules added inside it are
 * alternatives. Groups still combine with AND at the root, so a second group
 * narrows while a second rule inside one widens; the two headings on screen say
 * which is which.
 *
 * Safe to place because the builder drops a group that stays empty rather than
 * storing it — scaffolding never reaches the wire, and an untouched canvas is
 * still refused for holding no conditions.
 *
 * This is also the shape all but 0.3% of stored segments already have, so a
 * segment authored here is structurally what the builder this replaced wrote.
 */
const createEmptyDocument = (): Record<string, unknown> => ({ and: [{ or: [] }] })

interface SegmentQueryStoreProps {
    value: Record<string, unknown>
    fields: PQueryBuilderField[]
    onRuleCommit: (next: Record<string, unknown>) => void
    children: React.ReactNode
}

/**
 * The query store, standing inside the segment value context rather than around it.
 *
 * A row summary is a plain function the builder calls with a condition, so everything it needs from
 * outside that condition — a journey's name, the domain's currency, a notification's title — has to
 * be closed over when the registry of summaries is built. Building it here is what lets those
 * summaries re-read as the vocabularies arrive, instead of naming an id for the whole session.
 */
function SegmentQueryStore({ value, fields, onRuleCommit, children }: SegmentQueryStoreProps) {
    const valueSummaries = useSegmentValueSummaries()

    // The builder owns the controls that read a field's values but not the endpoints they come from, so
    // the read is handed to it. Without it a field that states its list is partial renders that partial
    // list as though it were the whole vocabulary, and a field that ships no list at all renders an
    // empty one — in both cases with no way to reach the value the reader wants.
    //
    // Undefined when no level can serve a read, which the builder treats as a field having only what
    // its catalog entry arrived with rather than as an empty vocabulary.
    const { loadFieldValues } = useSegmentValueInputContext()

    return (
        <PQueryBuilderProvider
            value={value}
            fields={fields}
            valueInputs={SEGMENT_VALUE_INPUTS}
            valueSummaries={valueSummaries}
            valueValidators={SEGMENT_VALUE_VALIDATORS}
            loadFieldValues={loadFieldValues}
            // Group descriptions are withheld while readers tell us the affordance reads as something
            // they have to fill in. Hiding the editor does not touch a description already stored on a
            // group, so this can be lifted again without anyone losing what they wrote.
            constraints={{ allowGroupDescriptions: false }}
            onRuleCommit={onRuleCommit}
        >
            {children}
        </PQueryBuilderProvider>
    )
}

/**
 * Composition layer between a segment and the generic PQuery builder.
 *
 * The builder owns the query model; this component owns the field catalog and
 * the level it is fetched for. Segmentation concerns reach the builder through
 * provider props — they are never merged into the generic builder itself.
 *
 * Nothing here mirrors what the builder knows. Whether the document is
 * representable, whether its conditions are complete, and what it serializes to
 * are read out of the builder at the moment a submit needs them, by the footer
 * and the notice that live inside the provider. This component owns only what
 * the builder has no view of: the name, the record being edited, whether that
 * record arrived at all, whether this level may write it, and whether a submit
 * has already been refused.
 *
 * Saving is a diff against the record as it was loaded, so an edit sends only the
 * fields it actually changed, and a segment nobody edited sends no request at all.
 * The record is the authority on everything it already holds — the consumer's
 * props seed a segment that does not exist yet, never one that does.
 */
export const SegmentQueryBuilder = forwardRef<SegmentQueryBuilderHandle, SegmentQueryBuilderProps>((props, ref) => {
    const { mode = 'standard', segmentId, templateId } = props
    // A duplicate opens from a source segment named by templateId, with no segmentId:
    // it seeds the working copy from that segment but saves as a new record. loadId is
    // whichever id the load fetches by — the segment being edited, or the source being
    // duplicated. isDuplicate is what tells the two apart everywhere the seeding, the
    // save mode and the multi-domain lock have to diverge.
    const loadId = segmentId ?? templateId
    const isDuplicate = templateId !== undefined && segmentId === undefined
    const containerFieldsSvc = useService(SegmentQueryFieldsService)
    const containerSegmentSvc = useService(SegmentService)
    const containerOrgSegmentSvc = useService(OrgSegmentService)
    const containerBatchSvc = useService(BatchService)
    const containerAccountSvc = useService(AccountService)
    const containerAppState = useService(AppState)

    const fieldsSvc: SegmentQueryFieldsSource = props.fieldsService ?? containerFieldsSvc
    const segmentSvc: DomainSegmentStore = props.segmentService ?? containerSegmentSvc
    const orgSegmentSvc: OrgSegmentStore = props.orgSegmentService ?? containerOrgSegmentSvc
    const batchSvc: OrgReachBatchService = props.batchService ?? containerBatchSvc
    const accountSvc: Pick<AccountService, 'fetchById'> = props.accountService ?? containerAccountSvc
    const channelSrc: SegmentChannelSource = props.channelSource ?? containerAppState

    const [catalog, setCatalog] = useState<SegmentQueryField[] | null>(null)
    const [name, setName] = useState(props.name ?? '')
    // The user's own channel edit, made through the selector. Undefined until they
    // touch it — an absence, not the empty list an explicit deselection writes. It is
    // the top of the precedence order below, so a local edit shows immediately, and
    // it is dropped when the consumer states a new external intent through
    // props.channels.
    const [channelOverride, setChannelOverride] = useState<DeliveryChannel[] | undefined>(undefined)
    // The channels the loaded segment targets, seeding the effective set only for the
    // standalone case. Lowest precedence, so a consumer that controls the channels
    // through props.channels is never overridden by the stored record.
    const [loadedChannels, setLoadedChannels] = useState<DeliveryChannel[] | undefined>(undefined)
    // The props.channels seen on the last render, so a change to it can drop a stale
    // selector edit (see the derivation below).
    const prevPropChannelsRef = useRef(props.channels)
    // The domains an org segment applies to. Owned here rather than on the model,
    // because a segment that does not exist yet has no record to carry them and the
    // model derives them from child records it will not have until it is saved. Seeded
    // from a loaded org record on load, reset to none for a new segment, and edited
    // through the org selector. Empty at the domain level, where it is unused.
    const [selectedDomainIds, setSelectedDomainIds] = useState<number[]>(NO_DOMAIN_IDS)
    // The user's own icon edit. Undefined until they touch it — an absence, not the null that
    // clearing the override writes — so a segment nobody edited keeps whatever icon its record holds.
    const [iconUrlOverride, setIconUrlOverride] = useState<string | null | undefined>(undefined)
    // What the icon uploader compresses to. Undefined until known, which the uploader reads as its
    // own default rather than as a quality of zero.
    const [orgMediaQuality, setOrgMediaQuality] = useState<number | undefined>(undefined)
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    /**
     * Reports why a save was refused — as a toast, and inline above the form — and returns the
     * `false` a refusing submit resolves with.
     *
     * Both surfaces, because they answer different questions. A segment with many conditions puts the
     * top of the form well off screen, and a refusal a reader cannot see is indistinguishable from a
     * button that did nothing; the toast reaches them wherever they have scrolled to. The inline copy
     * is what they read *while* acting on it — several of these name a specific condition, and a toast
     * is gone by the time they have scrolled to find it.
     *
     * Called on every refusal rather than derived from the held message. Reacting to the message
     * *changing* would leave a second attempt that fails the same way looking ignored, which is the
     * whole failure this exists to fix.
     */
    const refuseSubmit = useCallback((message: string): false => {
        setSubmitError(message)
        // Populated by the provider inside antd's <App>. A consumer rendering the builder outside it
        // loses the toast and keeps the inline notice — a degradation, not a failed save — so this
        // must never be the thing that throws mid-submit.
        antdStaticApi.notification?.error({ message: 'Segment not saved', description: message })
        return false
    }, [])
    // A segment that has to be fetched cannot be rendered before it arrives, so
    // an id present at mount starts this false.
    const [segmentLoaded, setSegmentLoaded] = useState(loadId === undefined)
    // True once a segment was asked for and did not arrive. Distinct from "not
    // loaded yet", because there is nothing left to wait for.
    const [loadFailed, setLoadFailed] = useState(false)
    // Set by the load when the record turns out to be one this level may not
    // write. Nothing outside the load ever clears it.
    const [multiDomainLock, setMultiDomainLock] = useState(false)

    // The calculated fields this level reports, as the endpoint returns them, and
    // whether they have been loaded. The domain endpoint answers with the domain's
    // own fields; the org endpoint answers with every domain's, each tagged with the
    // domain it belongs to. The unsafe-execution gate consults the reduced set below,
    // and until this reads loaded it must not judge a delayed-property segment either
    // way — so the gate stays off rather than treating a not-yet-loaded set as empty.
    const [rawCalculatedFields, setRawCalculatedFields] = useState<OrgCalculatedField[]>([])
    const [calculatedFieldsLoaded, setCalculatedFieldsLoaded] = useState(false)

    // Editing is off when the consumer says so, and also when the record itself
    // rules it out.
    const readonly = !!props.readonly || multiDomainLock

    /**
     * The document the builder re-initializes from.
     *
     * A ref, because the builder already holds the live model: routing every rule
     * commit through state would re-render the whole builder subtree for a value
     * the builder is itself the source of.
     *
     * It is kept current rather than left at the document the segment was opened
     * with, because a change to the field catalog can send the builder back here
     * to re-parse. Handing back the opened document would silently discard every
     * edit made since. It is also what the unsaved-changes question is asked about,
     * so a document that lagged behind the builder would call edited work pristine.
     */
    const documentRef = useRef<Record<string, unknown>>(createEmptyDocument())
    // The segment being edited and the pristine copy every save is diffed
    // against. Both are null for a segment that does not exist yet.
    const segmentRef = useRef<AnySegment | null>(null)
    const originalRef = useRef<AnySegment | null>(null)
    const footerRef = useRef<SegmentBuilderFooterHandle>(null)

    /**
     * Rebuilds the builder around a document this component has just put in place.
     *
     * Seeding the ref above is a mutation that renders nothing on its own, so
     * something has to carry the new document to the builder. A load has the
     * not-loaded-yet gate further down for that — the builder is unmounted while the
     * record is in flight and mounts around it when it lands. A segment id being
     * taken away has no such gate, and its reset must not depend on some other piece
     * of state happening to change as well. Bumping this is that remount, and as the
     * provider's key it makes the rebuild unconditional: a store still holding the
     * rows of the record just closed would save them as a new segment.
     */
    const [documentGeneration, setDocumentGeneration] = useState(0)

    /**
     * How to leave, read at the moment a load failure needs it.
     *
     * A ref, because the load must not depend on the identity of a consumer's
     * callback: a parent re-rendering with a fresh arrow would otherwise re-fetch
     * the segment.
     */
    const leaveRef = useRef(props.onCancel)
    useEffect(() => {
        leaveRef.current = props.onCancel
    }, [props.onCancel])

    /**
     * The name a segment that does not exist yet starts from, read at the moment the
     * builder goes back to being one.
     *
     * A ref for the same reason as above: the effect that reads it fetches the
     * segment, so depending on this prop would re-fetch a record on a name the
     * consumer only meant as a placeholder for the next new one.
     */
    const seedNameRef = useRef(props.name)
    useEffect(() => {
        seedNameRef.current = props.name
    }, [props.name])

    // The level owns which id is present, so these stand in for it as effect
    // dependencies — the union makes `props.domainId` / `props.orgId` unreachable
    // outside their own branch.
    const domainId = props.level === 'domain' ? props.domainId : undefined
    const orgId = props.level === 'org' ? props.orgId : undefined

    useEffect(() => {
        let cancelled = false

        const load = async () => {
            const next =
                props.level === 'domain'
                    ? await fieldsSvc.getDomainFieldsCached(props.domainId)
                    : await fieldsSvc.getAccountFieldsCached(props.orgId)

            if (!cancelled) setCatalog(next)
        }

        load()

        return () => {
            cancelled = true
        }
    }, [props.level, domainId, orgId, fieldsSvc])

    // Domain reach goes through the segment service's own estimate call; the org
    // surface has its own reach sibling further down. A stand-in service may omit the
    // method, so its presence is checked before it is used rather than assumed.
    const reachEnabled = props.level === 'domain' && typeof segmentSvc.estimateReachByQuery === 'function'

    useEffect(() => {
        let cancelled = false
        setCalculatedFieldsLoaded(false)

        // Null means this level's service exposes no calculated-fields lookup at
        // all, which is distinct from a lookup that ran and returned nothing.
        const load = async (): Promise<OrgCalculatedField[] | null> => {
            if (props.level === 'domain') {
                if (typeof segmentSvc.fetchCalculatedFieldsByDomainId !== 'function') return null
                const res = await segmentSvc.fetchCalculatedFieldsByDomainId(props.domainId)
                return res.ok ? (res.data ?? []) : []
            }

            if (typeof orgSegmentSvc.fetchCalculatedFields !== 'function') return null
            const res = await orgSegmentSvc.fetchCalculatedFields(props.orgId)
            return res.ok ? (res.data ?? []) : []
        }

        load().then((fields) => {
            if (cancelled) return
            // No lookup to consult, so the unsafe gate has nothing to judge against
            // and stays off — the same as when reach itself is unavailable. Leaving
            // it unloaded is deliberate: an empty set here would read every
            // delayed-property segment as unsafe on a surface that cannot compute it.
            if (fields === null) return
            setRawCalculatedFields(fields)
            // Marked loaded whether the fetch succeeded or not: a lookup that ran and
            // failed leaves the field set empty, which correctly reads a
            // delayed-property segment as unsafe rather than waving it through.
            setCalculatedFieldsLoaded(true)
        })

        return () => {
            cancelled = true
        }
    }, [props.level, domainId, orgId, segmentSvc, orgSegmentSvc])

    // The set the unsafe check reads. Domain level uses its fields as delivered. Org
    // level reduces the all-domain set to the shared fields across the selected
    // domains, nulling a field's timestamp when any selected domain has not computed
    // it — so adding a domain can turn a safe org segment unsafe.
    const calculatedFields = useMemo<ReachCalculatedField[]>(
        () =>
            props.level === 'org'
                ? reduceOrgCalculatedFields(rawCalculatedFields, selectedDomainIds)
                : rawCalculatedFields,
        [props.level, rawCalculatedFields, selectedDomainIds],
    )

    // The channels this level could target at all, gating whether a selector is
    // offered and bounding what it may choose. Domain level reads the domain's own
    // active channels; org level reads the parent account's — both off records the
    // app already holds, so no fetch stands between opening the builder and knowing
    // them.
    const enabledChannels = useMemo<DeliveryChannel[]>(() => {
        const domains = channelSrc.currentUserDomains
        if (!domains) return NO_CHANNELS

        if (props.level === 'domain') {
            const record = domains.find((domain) => domain.id === props.domainId)
            return record ? getEnabledDeliveryChannels(record, true) : NO_CHANNELS
        }

        const record = domains.find((domain) => domain.accountId === props.orgId)
        return record ? getAccountEnabledDeliveryChannels({ flags: record.accountFlags }) : NO_CHANNELS
    }, [channelSrc, props.level, domainId, orgId])

    // The selector is offered on the same terms as the legacy builder: only when the
    // consumer has not suppressed it and there is a real choice to make.
    const showChannelsSelector = !props.hideChannelsSelector && enabledChannels.length > 1

    // The domains an org segment may target: the current user's domains on this org's
    // account that the user can create a segment on. A domain on another account is
    // never the org's to target, and one the user lacks create rights on is not theirs
    // to add — both are filtered before the option is offered. Sorted the way the rest
    // of the app orders domain pickers.
    const domainOptions = useMemo<DropdownViewSelectOption<number>[]>(() => {
        if (props.level !== 'org') return NO_DOMAIN_OPTIONS

        const domains = channelSrc.currentUserDomains
        if (!domains) return NO_DOMAIN_OPTIONS

        const options = domains
            .filter((domain) => domain.accountId === props.orgId)
            .filter((domain) =>
                channelSrc.abilityStore.can(
                    AbilityAction.CREATE,
                    asCaslSubject(SubjectEntity.SEGMENT, { domainId: domain.id }),
                ),
            )
            .map((domain) => ({ value: domain.id, label: domain.displayName, title: domain.displayName }))

        options.sort(domainOptionsSort)
        return options
    }, [channelSrc, props.level, orgId])

    // The display names the org reach breakdown labels its per-domain rows with,
    // drawn from the same options the selector offers so nothing is fetched.
    const domainNames = useMemo<Record<number, string>>(() => {
        const names: Record<number, string> = {}
        for (const option of domainOptions) names[option.value] = option.label
        return names
    }, [domainOptions])

    // Org reach fans its per-domain requests through the batch service; a stand-in
    // may omit it, so its presence is checked before the sibling is offered.
    const orgReachEnabled = props.level === 'org' && typeof batchSvc.batch === 'function'

    // The org reach adapter bound to the injected batch service, so the sibling stays
    // a pure reader that neither knows the transport nor builds the batch itself.
    const runOrgReach = useCallback<EstimateOrgReach>(
        (ids, query, targetedChannels, opts) => estimateOrgReach(batchSvc, ids, query, targetedChannels, opts),
        [batchSvc],
    )

    // The channels that scope the view: the field catalog the builder is handed, the
    // reach estimate, and the selector's own value. props.channels, when the consumer
    // provides it, is authoritative and live and wins over the stored record; a local
    // selector edit sits above it until the consumer states a new intent, at which
    // point that edit is dropped so the new external value takes over.
    //
    // The reset runs in render, not an effect, so a props.channels change reaches
    // field-filtering on the same render it arrives — an effect lags a render and
    // would leave the catalog narrowed to the superseded selection.
    if (!deepEqual(prevPropChannelsRef.current, props.channels)) {
        prevPropChannelsRef.current = props.channels
        // Only a real edit needs clearing; the guard keeps this from looping on the
        // render it triggers.
        if (channelOverride !== undefined) setChannelOverride(undefined)
    }

    // What a segment targets when nothing else names it: the channels this level has
    // enabled. A create has to state its channels — the API requires them — and this
    // is the same set the selector draws, so what is shown and what is sent agree.
    // Empty means the records behind them have not resolved yet, which states nothing
    // and must not be mistaken for a choice.
    const defaultChannels = enabledChannels.length ? enabledChannels : undefined

    const viewChannels = channelOverride ?? props.channels ?? loadedChannels ?? defaultChannels
    // The server catalog is level-aware but selection-agnostic, so the targeted
    // channels narrow it further. An absent selection narrows nothing, leaving every
    // field visible; a chosen set narrows to the fields those channels carry.
    const channels = viewChannels ?? NO_CHANNELS
    // The org catalog narrows further to the fields the selected domains carry; an
    // empty selection narrows nothing, so every field stays visible until domains are
    // chosen. Domain level has a single domain and no such selection.
    const domainIds = props.level === 'org' ? selectedDomainIds : undefined
    const fields = useSegmentQueryFields(catalog ?? NO_CATALOG, { channels, domainIds })

    // Only the standard page offers the icon override; a drawer builds a segment for immediate use
    // and has no place to explain one.
    const iconOverrideOffered = mode === 'standard'

    // The org record is not among the domains the app already holds, so its media quality is read
    // from the account. Skipped where the control is not offered, so a drawer fetches nothing.
    useEffect(() => {
        if (props.level !== 'org' || !iconOverrideOffered) return

        let cancelled = false
        accountSvc.fetchById(props.orgId, undefined, false).then((account) => {
            if (!cancelled) setOrgMediaQuality(account?.mediaQuality)
        })

        return () => {
            cancelled = true
        }
    }, [props.level, orgId, iconOverrideOffered, accountSvc])

    const currentDomain = props.level === 'domain' ? containerAppState.currentDomain : undefined
    const iconMediaQuality =
        props.level === 'org'
            ? orgMediaQuality
            : currentDomain?.id === domainId
              ? currentDomain?.mediaQuality
              : undefined
    const iconUrl = iconUrlOverride !== undefined ? iconUrlOverride : (segmentRef.current?.getIconUrl() ?? null)

    // Each level persists through its own model class, and both accept the
    // camelCase records their service resolves.
    const buildSegmentModel = useCallback(
        (record: Record<string, unknown>): AnySegment =>
            props.level === 'domain' ? SegmentModel.build(record) : OrgSegmentModel.build(record),
        [props.level],
    )

    const buildEmptySegment = useCallback((): AnySegment => {
        const base = {
            name: props.name ?? '',
            source: props.source ?? SegmentSource.STANDARD,
            filters_json: createEmptyDocument(),
            // A create states its channels or the write is refused, so the
            // consumer's choice falls back to the enabled set rather than to
            // nothing. Still absent rather than empty when even that is unknown:
            // the DTO helpers drop an undefined value and keep an empty array, and
            // an empty array on the wire states a choice nobody made.
            channels: props.channels ?? defaultChannels,
        }

        return props.level === 'domain'
            ? SegmentModel.build({ ...base, domain_id: props.domainId })
            : OrgSegmentModel.build({ ...base, account_id: props.orgId })
    }, [props.level, domainId, orgId, props.name, props.source, props.channels, defaultChannels])

    useEffect(() => {
        if (loadId === undefined) {
            // Nothing is being edited any more, so nothing a record put here may
            // stay: the document, the name and the builder's rows all came from a
            // record that is no longer open, and a save that kept them would create
            // a second copy of it beside the original.
            segmentRef.current = null
            originalRef.current = null
            documentRef.current = createEmptyDocument()
            setDocumentGeneration((generation) => generation + 1)
            setName(seedNameRef.current ?? '')
            // Back to a new segment: forget the stored record's channels and any
            // selector edit made against it, so the effective set falls to
            // props.channels.
            setLoadedChannels(undefined)
            setChannelOverride(undefined)
            setIconUrlOverride(undefined)
            // A new org segment starts with no domains chosen — the selector supplies
            // them before it can be saved.
            setSelectedDomainIds(NO_DOMAIN_IDS)
            setSubmitAttempted(false)
            setSubmitError(null)
            setLoadFailed(false)
            setMultiDomainLock(false)
            setSegmentLoaded(true)
            return
        }

        let cancelled = false
        setSegmentLoaded(false)
        setLoadFailed(false)
        setMultiDomainLock(false)

        const load = async () => {
            // A forbidden fetch leaves the view rather than reporting in place:
            // the segment the caller named is not this user's to see, so there is
            // nothing on this screen for them to act on.
            const onForbidden = onResponseError403(() => leaveRef.current?.())

            const record =
                props.level === 'domain'
                    ? await segmentSvc.fetchSegmentById(props.domainId, loadId, false, undefined, onForbidden)
                    : getResponseRecord(
                          await orgSegmentSvc.fetch(props.orgId, loadId, { errorHandler: onForbidden }),
                      )

            if (cancelled) return

            if (!record) {
                // A segment was asked for and did not arrive. Carrying on would
                // leave an empty document with no record to diff against, so the
                // next save would create a second segment beside the one the user
                // opened to edit. The service has already said why it failed.
                setLoadFailed(true)
                setSegmentLoaded(true)
                return
            }

            const loaded = buildSegmentModel(record as Record<string, unknown>)

            // Seeding the ref rather than dispatching keeps the load out of
            // the dirty path: the document the builder opens with is the
            // stored one, so nothing has been edited yet.
            //
            // A stored app-version condition is collapsed onto one shared property
            // and scoped by meta.channel; the builder resolves it by the
            // channel-suffixed field key, so it is expanded on the way in and
            // collapsed again on the way out. The two are inverses on stored shapes,
            // so a segment nobody edited still diffs clean against the record.
            //
            // The document, name-seed source and channels open the same way whether
            // the record is being edited or duplicated: a duplicate is a copy of the
            // source segment, differing only in its name suffix and in saving as a
            // create rather than an update (below).
            documentRef.current = expandAppVersionChannels(loaded.getFiltersJson())
            // The stored record is what the standalone selector opens on. A consumer
            // that names channels through props.channels still wins over this for the
            // view (see the precedence below); the stored list is the fallback when it
            // does not. A freshly loaded record also discards any prior selector edit.
            setLoadedChannels(loaded.getChannels())
            setChannelOverride(undefined)
            setIconUrlOverride(undefined)

            if (isDuplicate) {
                // A duplicate saves as a new record. The working copy carries the
                // source's content with its id cleared, so a later save cannot address
                // the source, and the pristine baseline is left empty so the save is
                // planned as a create rather than an update.
                const duplicateName = `${loaded.getName() ?? ''} (Duplicate)`
                const working = buildSegmentModel({ ...loaded.serialize(), id: undefined })
                working.setName(duplicateName)
                segmentRef.current = working
                originalRef.current = null
                setName(duplicateName)

                // An org duplicate opens spanning only the domains this user may create
                // a segment on, so the copy is never seeded to span domains they could
                // not save it to.
                if (loaded instanceof OrgSegmentModel) {
                    setSelectedDomainIds(
                        loaded
                            .getDomainIds()
                            .filter((id) =>
                                channelSrc.abilityStore.can(
                                    AbilityAction.CREATE,
                                    asCaslSubject(SubjectEntity.SEGMENT, { domainId: id }),
                                ),
                            ),
                    )
                }

                // A duplicate of a multi-domain segment stays editable: it is becoming
                // a new segment, not editing the shared one, so the domain-level write
                // lock below does not apply to it.
            } else {
                segmentRef.current = loaded
                originalRef.current = loaded.clone()
                setName(loaded.getName() ?? '')
                // An org record states the domains it spans; the selector opens on them
                // so an edit diffs against the same set the save will send.
                if (loaded instanceof OrgSegmentModel) setSelectedDomainIds(loaded.getDomainIds())

                // A segment several domains share belongs to the organization above
                // them. Writing it through one domain's endpoint would set the shared
                // record's conditions from a single child's view of the catalog, so
                // this route may only show it.
                if (props.level === 'domain' && loaded.getIsMultiDomain()) {
                    setMultiDomainLock(true)
                }
            }

            setSegmentLoaded(true)
        }

        load()

        return () => {
            cancelled = true
        }
    }, [props.level, domainId, orgId, loadId, isDuplicate, segmentSvc, orgSegmentSvc, buildSegmentModel, channelSrc])

    const handleRuleCommit = useCallback((value: Record<string, unknown>) => {
        // A commit is not by itself an edit. The builder also reports back after
        // re-parsing the document it already holds, which a change to the field
        // catalog alone is enough to trigger — so what arrived has to be compared
        // to what is held rather than assumed to be new.
        const edited = !deepEqual(value, documentRef.current)

        documentRef.current = value

        if (!edited) return

        // Editing a condition withdraws whatever the last blocked submit
        // reported — the document it judged no longer exists. Both come back if
        // the next submit is blocked too. A re-parse leaves that document intact,
        // and with it the refusal, which still describes the rows on screen.
        setSubmitAttempted(false)
        setSubmitError(null)
    }, [])

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
    }, [])

    /**
     * Commits a new org domain selection, pruning the document first when the
     * change would strand a condition.
     *
     * Narrowing the domains narrows the field catalog, and a condition on a field
     * the new catalog no longer carries can neither be shown nor saved. So the
     * document as it stands is checked against the catalog the new selection would
     * produce: if nothing is stranded, the selection is applied at once; if
     * something is, the change is held behind a confirm. Continuing prunes the
     * stranded conditions and re-seeds the builder around the reduced document;
     * cancelling applies nothing, so the previous selection and the untouched
     * document both stand.
     *
     * A plain function rather than a memoized callback: it closes over the live
     * catalog, channels and document, and is only ever handed to the selector, so
     * a fresh identity each render costs nothing.
     */
    const applyDomainSelection = (nextIds: number[]) => {
        const nextFields = filterSegmentQueryFields(catalog ?? NO_CATALOG, { channels, domainIds: nextIds })
        const stranded = collectUnsupportedProperties(documentRef.current, nextFields)

        if (stranded.length === 0) {
            setSelectedDomainIds(nextIds)
            return
        }

        antdStaticApi.modal.confirm({
            title: 'Segment Fields Changed',
            // Present tense, and naming them. Nothing has been removed at this point — the prune runs
            // in `onOk` and Cancel leaves both the selection and the document untouched — so reporting
            // it as done told a reader who wanted to keep these conditions that they were already
            // gone, which is an argument for pressing Continue. And the consequence is dropping
            // conditions from a live segment, so the choice is only informed if it says which.
            content: (
                <>
                    <p>
                        Some conditions use fields the new domain selection does not include.
                        Continuing will remove them:
                    </p>
                    <ul>
                        {stranded.map((property) => (
                            <li key={property}>
                                {fields.find((field) => field.key === property)?.label ?? property}
                            </li>
                        ))}
                    </ul>
                </>
            ),
            okText: 'Continue',
            cancelText: 'Cancel',
            onOk: () => {
                // Drop the stranded conditions, then send the builder back to the
                // reduced document by bumping the generation it re-initializes from.
                documentRef.current = pruneUnsupportedConditions(documentRef.current, nextFields)
                setSelectedDomainIds(nextIds)
                setDocumentGeneration((generation) => generation + 1)
            },
        })
    }

    /**
     * Sends a payload through the service for the current level.
     *
     * The two services disagree on both signature and success convention, so
     * this is the only place either shape appears; everything downstream reads
     * the result through the normalizers in `save.ts`.
     */
    const persist = useCallback(
        async (saveMode: 'create' | 'update', dto: Record<string, unknown>, segment: AnySegment) => {
            if (props.level === 'domain') {
                return saveMode === 'create'
                    ? segmentSvc.createSegmentForDomainId(props.domainId, dto as any)
                    : segmentSvc.updateSegmentById(props.domainId, segment.getId(), dto as any)
            }

            return saveMode === 'create'
                ? orgSegmentSvc.create(props.orgId, dto, { showLoadingScreen: true })
                : orgSegmentSvc.update(props.orgId, { id: segment.getId(), name: segment.getName() }, dto, {
                      showLoadingScreen: true,
                  })
        },
        [props.level, domainId, orgId, segmentSvc, orgSegmentSvc],
    )

    /**
     * What a save of the given document would send, and whether it would send
     * anything at all.
     *
     * The single answer to both questions, because two callers need it for different
     * reasons — the save, to decide what to write, and the unsaved-changes guard, to
     * decide whether leaving would lose work. Derived rather than tracked: a guard
     * reading a flag of its own is free to disagree with the save, and a change that
     * is persistable and pristine at the same time is one an exit path discards
     * without asking.
     *
     * `holdsChange` is asked of the state the builder opened in, whether that came
     * from a record or from the consumer's props, so a segment nobody has touched
     * answers no either way. It is not the same question as the mode: a create still
     * states the whole record, while an update carries only the fields that differ.
     */
    const planSave = useCallback(
        (document: Record<string, unknown>) => {
            const loaded = originalRef.current
            const baseline = loaded ?? buildEmptySegment()
            const next = (segmentRef.current ?? baseline).clone()
            next.setName(name.trim())
            // Only a local selector edit overrides the channels the working copy
            // already carries — a new segment's from its props-seeded baseline, a
            // loaded one's from the stored record. props.channels scopes the view but
            // is never written onto a segment that already states its own channels, so
            // a consumer naming channels beside a loaded id reports no channel change.
            if (channelOverride !== undefined) next.setChannels(channelOverride)
            if (iconUrlOverride !== undefined) next.setIconUrl(iconUrlOverride)
            // Collapse the channel-suffixed app-version keys the builder emits back onto
            // the shared property the engine stores under, restoring meta.channel. This
            // inverts the expansion applied on load, so an untouched segment collapses to
            // exactly the stored document and the diff below finds nothing to send.
            next.setFiltersJson(collapseAppVersionChannels(document))

            // The org endpoints take the domain set on every write; it comes from the
            // selector rather than the record, so a create can state it and an edit
            // diffs against what was loaded. Its presence is also what selects the
            // org DTO shape, so it stays absent at the domain level.
            const domainIds = props.level === 'org' ? selectedDomainIds : undefined

            const plan = buildSegmentSaveDto({ segment: next, original: loaded ?? undefined, domainIds })
            const againstBaseline = buildSegmentSaveDto({ segment: next, original: baseline, domainIds })

            return { ...plan, segment: next, holdsChange: hasSaveChanges(againstBaseline.dto) }
        },
        [name, channelOverride, iconUrlOverride, buildEmptySegment, props.level, selectedDomainIds],
    )

    /**
     * The only path to persistence. Both the footer's Save and the imperative
     * handle arrive here with what the builder reports about its own document.
     */
    const handleSubmit = useCallback(
        async (submission: BuilderSubmission): Promise<boolean> => {
            // A readonly builder offers no submit control, and the imperative
            // handle must not become the one it lacks.
            if (readonly) return false

            // A document the builder could not model, it also cannot serialize:
            // it falls back to an empty condition set, so saving would replace a
            // targeted segment with one that matches everything. Refusing is the
            // lesser harm — a declined edit can be retried, but the conditions
            // overwritten by that save are not recoverable from the record that
            // replaced them.
            //
            // This precedes the field checks because it is not a mistake in what
            // the user entered, and no edit offered here can clear it — which is
            // also why it does not mark up the fields.
            if (!submission.supported) {
                return refuseSubmit(
                    "This segment includes conditions that aren't supported by this builder, so it cannot be saved here.",
                )
            }

            // An org-level segment is defined by the domains it spans, and the org
            // endpoints take that set on every write. A segment targeting no domain
            // reaches nobody, so a save with an empty selection is refused rather than
            // sent with nothing to apply to.
            //
            // Like the check above, this is not a mistake in what the user entered, so
            // it does not mark up any field.
            if (props.level === 'org' && selectedDomainIds.length === 0) {
                return refuseSubmit(
                    'An organization segment needs the domains it applies to. Select at least one domain to save it.',
                )
            }

            setSubmitAttempted(true)

            const check = canSubmitSegment({
                name,
                builderValid: submission.valid,
                conditionCount: submission.conditionCount,
            })

            // A count with nothing to count matches nobody. A count with no window is worse — it
            // reads as complete on screen and the engine takes it over the subscriber's whole
            // history, so the segment quietly matches a wider audience than the row shows. Neither
            // can reach a save.
            //
            // The rows themselves also carry these as errors, through the registered validators, so
            // the builder marks the ones at fault. This still runs, and runs *before* the builder's
            // own verdict is turned into a message: "correct the highlighted conditions" is true but
            // says less than naming the condition types, and a document loaded from storage whose
            // rows the reader never opened has to be caught here regardless.
            const incomplete =
                describeMissingExpressions(findConditionsMissingExpression(submission.document, fields)) ??
                describeMissingWindows(findConditionsMissingWindow(submission.document, fields))

            if (!check.ok) {
                return refuseSubmit(incomplete ?? check.message ?? 'This segment cannot be saved yet.')
            }

            if (incomplete) {
                return refuseSubmit(incomplete)
            }

            setSubmitError(null)

            // A segment on a delayed property the platform has not yet computed cannot be sized or
            // targeted until that computation runs. That is a reason to refuse it *here* — the
            // notification drawer, where the segment is being created to target immediately, so one
            // that cannot be targeted is no use. It is not a reason to refuse it on its own page: the
            // segment is worth keeping and becomes targetable once the property is computed, which is
            // why the reach estimate explains the missing size rather than the save refusing.
            //
            // The check waits until the calculated fields are known — before then it cannot tell safe
            // from unsafe, and refusing on the unknown would block a legitimate save during the brief
            // load. Both levels judge against `calculatedFields`, which is already reduced to the
            // selected domains at the org level.
            if (
                mode === 'drawer' &&
                calculatedFieldsLoaded &&
                isUnsafeExecution(submission.document, calculatedFields)
            ) {
                return refuseSubmit(getUnsafeExecutionText({ isDrawerMode: true }))
            }

            const { mode: saveMode, dto, segment: next } = planSave(submission.document)

            // An untouched segment needs no request: the diff found nothing the
            // stored record does not already hold.
            if (saveMode === 'update' && !hasSaveChanges(dto)) {
                props.onSaved?.(next)
                return true
            }

            // An update is addressed to a stored record, and there is nothing here
            // to address it with — a create that reported no id, or a record that
            // arrived without a usable one. Sending it anyway builds a path around
            // the missing value and PATCHes a record that does not exist, which the
            // services report the way they report any other write: as a success. The
            // user would be told the edit is stored and would leave the only screen
            // still holding it.
            if (saveMode === 'update' && toSegmentId(next.getId()) === undefined) {
                return refuseSubmit(
                    'This segment cannot be identified, so the change cannot be applied to it. Reopen the segment from the list and try again.',
                )
            }

            const result = await persist(saveMode, dto, next)

            // Both services raise their own failure notification, so adding one
            // here would only repeat it. The edits stay in place and stay dirty.
            if (!didSaveSucceed(result)) return false

            // What was just sent is now the pristine state, and the response is
            // read for one thing only: the id of a record that did not exist
            // before, without which a later save from this same builder would
            // create a second segment.
            //
            // Taking the rest of the saved state from the response instead would
            // make a clean form depend on the service echoing the payload back.
            // Nothing guarantees that — a write may answer with a partial record
            // or none at all — and every field it left out would read as an unsaved
            // edit, so a successful save would go on prompting about work it had
            // already stored. At the org level it costs more still: the domain set
            // lives on the loaded record and rides on every write, so adopting an
            // answer without one would leave the next save unable to state it.
            const persisted = buildSegmentModel({
                ...next.serialize(),
                id: getResponseRecordId(result) ?? next.getId(),
            })

            segmentRef.current = persisted
            originalRef.current = persisted.clone()
            props.onSaved?.(persisted)

            return true
        },
        [
            name,
            readonly,
            props.level,
            fields,
            planSave,
            buildSegmentModel,
            persist,
            props.onSaved,
            calculatedFields,
            calculatedFieldsLoaded,
            selectedDomainIds,
            refuseSubmit,
        ],
    )

    // The document the builder last reported is the one a save would send, so it is
    // the one the guard asks about.
    const isDirty = useCallback(() => planSave(documentRef.current).holdsChange, [planSave])
    const { confirmLeave } = useUnsavedChangesGuard(isDirty)

    const handleCancel = useCallback(() => {
        confirmLeave(() => props.onCancel?.())
    }, [confirmLeave, props.onCancel])

    const dismissSubmitError = useCallback(() => setSubmitError(null), [])

    // A control whose vocabulary is fetched a page at a time has to know what the
    // query already names, and the live document is the only place that says so.
    const readDocument = useCallback(() => documentRef.current, [])

    useImperativeHandle(
        ref,
        () => ({
            // The footer reads the builder, so it is what runs a submit. Before
            // it exists there is no document to submit, and nothing was saved.
            submit: async () => (await footerRef.current?.submit()) ?? false,
            isDirty,
        }),
        [isDirty],
    )

    /*
     * The segment the caller named never arrived, so there is nothing to edit.
     * Rendering the builder anyway would put an empty create form where the user
     * opened an edit, and the save from it would go on to duplicate the segment
     * they were trying to change.
     */
    if (loadFailed) {
        return <Alert type="error" title="This segment could not be loaded, so it cannot be edited here." />
    }

    // The builder cannot offer a property picker before it knows the properties,
    // and it cannot open a stored segment before that segment arrives — mounting it
    // early would give it the empty document to parse and then leave the record with
    // nowhere to land. A failed load settles as an empty catalog rather than null, so
    // this never waits forever.
    if (catalog === null || !segmentLoaded) return null

    return (
        <SegmentValueInputProvider
            level={props.level}
            domainId={domainId}
            orgId={orgId}
            fields={fields}
            getDocument={readDocument}
        >
            <SegmentQueryStore
                key={documentGeneration}
                value={documentRef.current}
                fields={fields}
                onRuleCommit={handleRuleCommit}
            >
                <Flex vertical gap={VERTICAL_WELL_GAP}>
                    {/* The only place a blocked submit surfaces its reason, so it has
                    to render for every rejection — including the ones that also
                    mark up a field or a condition row. */}
                    {submitError && (
                        <Alert type="error" title={submitError} closable={{ onClose: dismissSubmitError }} />
                    )}

                    <ReadOnlyNotice multiDomainLock={multiDomainLock} />

                    <Well title="Details">
                        <label htmlFor="segment-query-builder-name">Name</label>
                        <Input
                            id="segment-query-builder-name"
                            value={name}
                            onChange={handleNameChange}
                            placeholder="Enter segment name"
                            maxLength={255}
                            disabled={readonly}
                            status={submitAttempted && !name.trim() ? 'error' : undefined}
                        />

                        {iconOverrideOffered && (
                            <SegmentIconField
                                level={props.level}
                                ownerId={props.level === 'domain' ? props.domainId : props.orgId}
                                mediaQuality={iconMediaQuality}
                                iconUrl={iconUrl}
                                readonly={readonly}
                                onChange={setIconUrlOverride}
                            />
                        )}
                    </Well>

                    {showChannelsSelector && (
                        <DeliveryChannelSelector
                            type="multiple"
                            value={viewChannels}
                            onChange={setChannelOverride}
                            visibleChannels={enabledChannels}
                            disabled={readonly}
                            wellProps={{ mode: mode === 'drawer' ? 'ghost' : 'default' }}
                        />
                    )}

                    {props.level === 'org' && (
                        <Well title="Domains" mode={mode === 'drawer' ? 'ghost' : 'default'}>
                            {/* readonly does not disable the domain selection: the
                            domains an org segment targets stay changeable even when the
                            rest of the segment is only being viewed. */}
                            <DomainAudienceSelect
                                value={selectedDomainIds}
                                domainSelectorOptions={domainOptions}
                                onChange={applyDomainSelection}
                                onClear={() => applyDomainSelection(NO_DOMAIN_IDS)}
                                handleDomainIdsChange={applyDomainSelection}
                            />
                        </Well>
                    )}

                    {/* Directly above the conditions rather than inside them: it is about the
                    conditions builder, so it sits against the thing it describes, but it is not part
                    of the segment being edited and should not read as though it were. */}
                    {props.notice}

                    <SegmentBuilderFooter
                        ref={footerRef}
                        readonly={readonly}
                        mode={mode}
                        submitAttempted={submitAttempted}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        actions={
                            reachEnabled && domainId !== undefined ? (
                                <SegmentReachEstimate
                                    domainId={domainId}
                                    targetedChannels={channels}
                                    calculatedFields={calculatedFields}
                                    estimateReach={segmentSvc.estimateReachByQuery}
                                />
                            ) : orgReachEnabled && orgId !== undefined ? (
                                <SegmentOrgReachEstimate
                                    orgId={orgId}
                                    domainIds={selectedDomainIds}
                                    targetedChannels={channels}
                                    calculatedFields={calculatedFields}
                                    domainNames={domainNames}
                                    estimateOrgReach={runOrgReach}
                                />
                            ) : undefined
                        }
                    />
                </Flex>
            </SegmentQueryStore>
        </SegmentValueInputProvider>
    )
})
